import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatLocal } from './utils/timezones';
import { useHOA } from './HOAApp';

const BoardRegistrationCodes = () => {
  const { hoaData } = useHOA();
  const hoaTimezone = hoaData?.timezone || 'US/Central';
  const [emails, setEmails] = useState('');
  const [creating, setCreating] = useState(false);
  const [sharedCreating, setSharedCreating] = useState(false);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadCodes = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/board/registration-codes');
      setCodes(res.data?.codes || []);
    } catch (e) {
      console.error('Failed to load codes', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCodes(); }, []);

  const createIndividual = async () => {
    setError(''); setSuccess(''); setCreating(true);
    try {
      const list = emails.split(/[\n,;\s]+/).map(e => e.trim()).filter(Boolean);
      if (!list.length) { setError('Please enter at least one email'); setCreating(false); return; }
      const res = await axios.post('/api/board/registration-codes/individual', { emails: list, send_emails: false });
      if (res.data?.success) {
        setSuccess(`Created ${res.data.codes?.length || list.length} codes`);
        setEmails('');
        loadCodes();
      } else {
        setError(res.data?.message || 'Failed to create codes');
      }
    } catch (e) {
      setError('Failed to create codes');
    } finally {
      setCreating(false);
    }
  };

  const createShared = async () => {
    setSharedCreating(true); setError(''); setSuccess('');
    try {
      const res = await axios.post('/api/board/registration-codes/shared', {});
      if (res.data?.success) {
        setSuccess('Shared code created');
        loadCodes();
      } else {
        setError(res.data?.message || 'Failed to create shared code');
      }
    } catch (e) {
      setError('Failed to create shared code');
    } finally {
      setSharedCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Create Individual Registration Codes</h3>
        <p className="text-sm text-gray-600 mb-2">Enter one or more emails, separated by commas or new lines.</p>
        <textarea className="w-full border rounded p-2" rows="4" value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="email1@example.com, email2@example.com" />
        <div className="mt-3">
          <button onClick={createIndividual} className="px-4 py-2 rounded bg-indigo-600 text-white" disabled={creating}>{creating ? 'Creating...' : 'Create Codes'}</button>
        </div>
        {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
        {success && <p className="text-green-600 text-sm mt-2">{success}</p>}
      </div>

      <div className="bg-white shadow rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Create Shared Registration Code</h3>
        <button onClick={createShared} className="px-4 py-2 rounded bg-indigo-600 text-white" disabled={sharedCreating}>{sharedCreating ? 'Creating...' : 'Create Shared Code'}</button>
      </div>

      <div className="bg-white shadow rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Existing Codes</h3>
        {loading ? (
          <p className="text-sm text-gray-600">Loading codes...</p>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="p-2">Code</th>
                  <th className="p-2">Type</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Expires</th>
                  <th className="p-2">Uses</th>
                  <th className="p-2">Email (if individual)</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.id} className="border-t">
                    <td className="p-2 font-mono">{c.code}</td>
                    <td className="p-2 capitalize">{c.code_type}</td>
                    <td className="p-2">{c.is_active ? 'Active' : 'Inactive'}</td>
                    <td className="p-2">{c.expires_at ? formatLocal(c.expires_at, hoaTimezone, 'MMM d, yyyy') : '-'}</td>
                    <td className="p-2">{(c.current_uses || 0)}/{(c.max_uses || (c.code_type === 'individual' ? 1 : '∞'))}</td>
                    <td className="p-2">{c.email || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BoardRegistrationCodes;